import type { T_JsonValue } from "@lattice/core-lib/types/chat-protocol";
import {
  CHAT_V8_COMPANY_ANALYSIS_FLOW_NAME,
  z_ChatV8ElicitationAnswerEventPayload,
  z_ChatV8ElicitationRequestPayload,
  z_ChatV8ElicitationWaitingPayload,
  type T_ChatV8ElicitationAnswer,
  type T_ChatV8ElicitationRequestPayload,
} from "@lattice/core-lib/types/chat-v8";
import type { EventObject, MetaObject, SetupTypes, SnapshotFrom } from "xstate";
import { assign, setup } from "xstate";

import {
  compareChatV8Events,
  type T_ChatV8RenderableItem,
} from "@lattice/core-lib/chat-v8/materialize";

const MANUAL_OTHER_OPTION_CUID = "manual_other";
const CHAT_V8_ELICITATION_AUTO_SUBMIT_DELAY_MS = 480;

type T_ChatV8ElicitationCallbacks = {
  onAnswer: (answer: T_ChatV8ElicitationAnswer) => void;
};

type T_ChatV8ElicitationCallbackRef = {
  current: T_ChatV8ElicitationCallbacks;
};

type T_ChatV8ElicitationMachineInput = {
  callbacks: T_ChatV8ElicitationCallbackRef;
  request: T_ChatV8ElicitationRequestPayload;
};

type T_ChatV8ElicitationMachineContext = {
  callbacks: T_ChatV8ElicitationCallbackRef;
  formFields: Record<string, T_JsonValue>;
  freeText: string;
  multiSelected: string[];
  request: T_ChatV8ElicitationRequestPayload;
  selectedOption: string | null;
};

type T_ChatV8ElicitationMachineEvent =
  | { fieldCuid: string; value: T_JsonValue; type: "FORM_FIELD_CHANGED" }
  | { fieldCuid: string; optionCuid: string; type: "FORM_OPTION_SELECTED" }
  | { text: string; type: "FREE_TEXT_CHANGED" }
  | { optionCuid: string; type: "MULTI_OPTION_TOGGLED" }
  | { optionCuid: string; type: "SINGLE_OPTION_SELECTED" }
  | { answer: T_ChatV8ElicitationAnswer; type: "SUBMIT_ANSWER" }
  | { type: "SUBMIT_FORM" }
  | { type: "SUBMIT_FREE_TEXT" }
  | { type: "SUBMIT_MULTI_SELECT" };

const elicitationSetupTypes: SetupTypes<
  T_ChatV8ElicitationMachineContext,
  T_ChatV8ElicitationMachineEvent,
  Record<never, string>,
  string,
  T_ChatV8ElicitationMachineInput,
  unknown,
  EventObject,
  MetaObject
> = {};

export const chatV8ElicitationMachine = setup({
  types: elicitationSetupTypes,
  actions: {
    applyFormField: assign(({ context, event }) => {
      if (event.type !== "FORM_FIELD_CHANGED") return {};
      return {
        formFields: {
          ...context.formFields,
          [event.fieldCuid]: event.value,
        },
      };
    }),
    applyFormSelection: assign(({ context, event }) => {
      if (event.type !== "FORM_OPTION_SELECTED") return {};
      return {
        formFields: {
          ...context.formFields,
          [event.fieldCuid]: event.optionCuid,
        },
      };
    }),
    applyFreeText: assign(({ event }) => {
      if (event.type !== "FREE_TEXT_CHANGED") return {};
      return { freeText: event.text };
    }),
    applyMultiSelection: assign(({ context, event }) => {
      if (event.type !== "MULTI_OPTION_TOGGLED") return {};
      return {
        multiSelected: context.multiSelected.includes(event.optionCuid)
          ? context.multiSelected.filter((cuid) => cuid !== event.optionCuid)
          : [...context.multiSelected, event.optionCuid],
      };
    }),
    applySingleSelection: assign(({ event }) => {
      if (event.type !== "SINGLE_OPTION_SELECTED") return {};
      return { selectedOption: event.optionCuid };
    }),
    submitAnswer: ({ context, event }) => {
      if (event.type !== "SUBMIT_ANSWER") return;
      context.callbacks.current.onAnswer(event.answer);
    },
    submitForm: ({ context }) => {
      context.callbacks.current.onAnswer({
        fields: context.formFields,
        kind: "form",
      });
    },
    submitFreeText: ({ context }) => {
      context.callbacks.current.onAnswer({
        kind: "free_text",
        text: context.freeText.trim(),
      });
    },
    submitMultiSelect: ({ context }) => {
      context.callbacks.current.onAnswer({
        kind: "multi_select",
        selectedOptionCuids: context.multiSelected,
      });
    },
    submitSingleSelect: ({ context, event }) => {
      if (event.type !== "SINGLE_OPTION_SELECTED") return;
      context.callbacks.current.onAnswer({
        kind: "single_select",
        selectedOptionCuid: event.optionCuid,
      });
    },
  },
  guards: {
    formIsComplete: ({ context }) =>
      isChatV8ElicitationFormComplete(context.request, context.formFields),
    formWillAutoSubmit: ({ context, event }) => {
      const fields = getNextFormFields(context.formFields, event);
      return (
        fields !== null &&
        shouldAutoSubmitChatV8ElicitationForm(context.request) &&
        isChatV8ElicitationFormComplete(context.request, fields)
      );
    },
    freeTextHasContent: ({ context }) => context.freeText.trim().length > 0,
    multiSelectIsValid: ({ context }) =>
      !context.request.required || context.multiSelected.length > 0,
  },
}).createMachine({
  id: "chatV8Elicitation",
  context: ({ input }) => ({
    callbacks: input.callbacks,
    formFields: {},
    freeText: "",
    multiSelected: [],
    request: input.request,
    selectedOption: null,
  }),
  initial: "editing",
  states: {
    editing: {
      on: {
        FORM_FIELD_CHANGED: { actions: "applyFormField" },
        FORM_OPTION_SELECTED: [
          {
            actions: "applyFormSelection",
            guard: "formWillAutoSubmit",
            target: "formCompletePending",
          },
          { actions: "applyFormSelection" },
        ],
        FREE_TEXT_CHANGED: { actions: "applyFreeText" },
        MULTI_OPTION_TOGGLED: { actions: "applyMultiSelection" },
        SINGLE_OPTION_SELECTED: {
          actions: ["applySingleSelection", "submitSingleSelect"],
          target: "submitted",
        },
        SUBMIT_ANSWER: { actions: "submitAnswer", target: "submitted" },
        SUBMIT_FORM: {
          actions: "submitForm",
          guard: "formIsComplete",
          target: "submitted",
        },
        SUBMIT_FREE_TEXT: {
          actions: "submitFreeText",
          guard: "freeTextHasContent",
          target: "submitted",
        },
        SUBMIT_MULTI_SELECT: {
          actions: "submitMultiSelect",
          guard: "multiSelectIsValid",
          target: "submitted",
        },
      },
    },
    formCompletePending: {
      after: {
        [CHAT_V8_ELICITATION_AUTO_SUBMIT_DELAY_MS]: {
          actions: "submitForm",
          target: "submitted",
        },
      },
      on: {
        FORM_FIELD_CHANGED: {
          actions: "applyFormField",
          target: "editing",
        },
        FORM_OPTION_SELECTED: [
          {
            actions: "applyFormSelection",
            guard: "formWillAutoSubmit",
            reenter: true,
            target: "formCompletePending",
          },
          { actions: "applyFormSelection", target: "editing" },
        ],
      },
    },
    submitted: {},
  },
});

type T_ChatV8ElicitationViewState = {
  disabled: boolean;
  formFields: Record<string, T_JsonValue>;
  freeText: string;
  multiSelected: string[];
  selectedOption: string | null;
};

export function getChatV8ElicitationViewState(
  snapshot: SnapshotFrom<typeof chatV8ElicitationMachine>,
  answered: T_ChatV8ElicitationAnswer | null,
): T_ChatV8ElicitationViewState {
  return {
    disabled: answered !== null || snapshot.matches("submitted"),
    formFields:
      answered?.kind === "form" ? answered.fields : snapshot.context.formFields,
    freeText:
      answered?.kind === "free_text"
        ? answered.text
        : snapshot.context.freeText,
    multiSelected:
      answered?.kind === "multi_select"
        ? answered.selectedOptionCuids
        : snapshot.context.multiSelected,
    selectedOption:
      answered?.kind === "single_select"
        ? answered.selectedOptionCuid
        : snapshot.context.selectedOption,
  };
}

function getNextFormFields(
  current: Record<string, T_JsonValue>,
  event: T_ChatV8ElicitationMachineEvent,
): Record<string, T_JsonValue> | null {
  if (event.type === "FORM_FIELD_CHANGED") {
    return { ...current, [event.fieldCuid]: event.value };
  }
  if (event.type === "FORM_OPTION_SELECTED") {
    return { ...current, [event.fieldCuid]: event.optionCuid };
  }
  return null;
}

export function shouldAutoSubmitChatV8ElicitationForm(
  request: T_ChatV8ElicitationRequestPayload,
): boolean {
  const fields = request.fields ?? [];
  return (
    fields.length > 0 && fields.every((field) => field.kind === "single_select")
  );
}

export function isChatV8ElicitationFormComplete(
  request: T_ChatV8ElicitationRequestPayload,
  fields: Record<string, T_JsonValue>,
): boolean {
  return (request.fields ?? []).every((field) => {
    if (!field.required) return true;
    const value = fields[field.fieldCuid];
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined;
  });
}

export function parseChatV8ElicitationRequest(
  payload: unknown,
): T_ChatV8ElicitationRequestPayload | null {
  const parsed = z_ChatV8ElicitationRequestPayload.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

export function getLatestChatV8ElicitationAnswer(
  item: T_ChatV8RenderableItem,
): T_ChatV8ElicitationAnswer | null {
  const sortedEvents = [...item.events].sort(compareChatV8Events);
  for (let index = sortedEvents.length - 1; index >= 0; index -= 1) {
    const event = sortedEvents[index];
    if (!event || event.type !== "elicitation.answered") continue;
    const parsed = z_ChatV8ElicitationAnswerEventPayload.safeParse(
      event.payload,
    );
    if (parsed.success) return parsed.data.answer;
  }
  return null;
}

/** The deterministic flow identity, not prompt copy or payload metadata, owns UI. */
export function isCompanyAnalysisElicitation(
  item: T_ChatV8RenderableItem,
): boolean {
  return item.events.some((event) => {
    if (event.type !== "run.waiting_for_input") return false;
    const parsed = z_ChatV8ElicitationWaitingPayload.safeParse(event.payload);
    return (
      parsed.success &&
      "flowName" in parsed.data &&
      parsed.data.flowName === CHAT_V8_COMPANY_ANALYSIS_FLOW_NAME
    );
  });
}

/** Submit historical single-select company asks through their existing other path. */
export function buildStockAutocompleteAnswer(
  request: T_ChatV8ElicitationRequestPayload,
  text: string,
): T_ChatV8ElicitationAnswer {
  const trimmed = text.trim();
  if (request.mode === "single_select") {
    return {
      kind: "single_select",
      otherText: trimmed,
      selectedOptionCuid: MANUAL_OTHER_OPTION_CUID,
    };
  }
  return { kind: "free_text", text: trimmed };
}

export function getStockAutocompleteAnswerText(args: {
  answer: T_ChatV8ElicitationAnswer;
  request: T_ChatV8ElicitationRequestPayload;
}): string | null {
  if (args.answer.kind === "free_text") return args.answer.text;
  if (args.answer.kind !== "single_select") return null;
  if (args.answer.otherText?.trim()) return args.answer.otherText.trim();

  const selectedOptionCuid = args.answer.selectedOptionCuid;
  const selected = (args.request.options ?? []).find(
    (option) => option.optionCuid === selectedOptionCuid,
  );
  return typeof selected?.value === "string"
    ? selected.value
    : (selected?.label ?? null);
}
